import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CircularProgress, Skeleton } from '@mui/material';
import { EndStyle, PostBox, UserProfile } from 'pages/home/Home.style';
import {
  BasicIconButton,
  Button,
  CustomPopover,
  Tab,
  Virtuoso,
} from 'components';
import {
  handleDeleteNotification,
  handleGetNotifications,
  handleUpdateNotificationLastSeen,
  handleUpdateNotificationRead,
} from 'redux/features/NotificationSlice';
import {
  NotificationContainer,
  NotificationMenuContainer,
} from './Notification.style';
import { useNavigate, useParams } from 'react-router-dom';
import { ReactComponent as Profile } from 'assert/images/Common/profileImg.svg';
import { ReactComponent as More } from 'assert/images/Home/moreHorizantal.svg';
import { ReactComponent as DeleteIcon } from 'assert/images/More/delete.svg';
import { ReactComponent as SampleCompany } from 'assert/images/Jobs/sampleCompany.svg';
import moment from 'moment';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from 'utils/helper';

function NotificationList({ tabNameLower }) {
  const currentTime = moment().utc().format('YYYY-MM-DD HH:mm:ss');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const allowBlock = useRef(true);

  const { notificationCount } = useSelector(
    (state) => state?.NotificationSlice
  );

  const [notificationList, setNotificationList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [isHasMore, setIsHasMore] = useState(false);
  const [scrollData, setScrollData] = useState({ page: 0 });
  const [openPopover, setOpenPopover] = useState(false);
  const [isNewNotification, setIsNewNotification] = useState(false);

  const [stopCalling, setStopCalling] = useState(false);
  const [notificationInitialCount, setNotificationInitialCount] =
    useState(false);

  useEffect(() => {
    const payload = {
      pageName: 'notification',
      last_viewed_at: currentTime,
    };
    dispatch(handleUpdateNotificationLastSeen(payload));
  }, []);

  useEffect(() => {
    if (notificationCount?.count !== undefined && !stopCalling) {
      setNotificationInitialCount(notificationCount?.count);
      setStopCalling(true);
    }
  }, [notificationCount?.count]);

  useEffect(() => {
    if (stopCalling && notificationInitialCount !== notificationCount?.count) {
      setIsNewNotification(true);
    }
  }, [notificationCount?.count, notificationInitialCount]);

  const handlefetch = (page) => {
    const payload = {
      tab: tabNameLower,
      page: page !== undefined ? page : scrollData.page,
      size: 15,
      currentTime,
    };
    dispatch(handleGetNotifications(payload))
      .then((res) => {
        setIsHasMore(res?.data?.hasMore);
        if (res?.data?.rows?.data?.length > 0) {
          setNotificationList((prev) => [...prev, ...res?.data?.rows?.data]);
        }
        setLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setLoading(false);
      });
  };

  useEffect(() => {
    if (allowBlock.current) {
      allowBlock.current = false;
      setLoading(true);
      handlefetch();
      setScrollData({ page: 1 });
    }
  }, []);

  const fetchMoreData = () => {
    setScrollData((prev) => ({
      ...prev,
      page: prev.page + 1,
    }));
    handlefetch();
  };

  const handleDelete = (id) => {
    setDeleteLoading(true);
    dispatch(handleDeleteNotification(id))
      .then(() => {
        setNotificationList(notificationList.filter((item) => item.id !== id));
        setDeleteLoading(false);
        setOpenPopover(false);
      })
      .catch(() => {
        setDeleteLoading(false);
        setOpenPopover(false);
      });
  };

  const notificationMenu = (id) => {
    return (
      <NotificationMenuContainer>
        <div
          className="notification-menu-item"
          onClick={() => (deleteLoading ? {} : handleDelete(id))}
        >
          <DeleteIcon className="notification-delete-icon" />
          <div>{t('Notification.Delete')}</div>
          {deleteLoading && (
            <CircularProgress
              size={22}
              className="notification-circular-icon"
            />
          )}
        </div>
      </NotificationMenuContainer>
    );
  };

  const handleRenderTitleMsg = (item) => {
    return (
      <>
        {item?.entity_type === 'post' ? (
          <div className="notification-message-container">
            <div
              className="notification-message"
              onClick={() => navigate(`/profile/${item.actionBy}`)}
            >
              {item.actionBy}
            </div>
            <span>
              {t(`Notification.${item.action}`)}{' '}
              {['comment', 'like', 'reply'].includes(item.action)
                ? t('Notification.onYourPost')
                : item.action === 'mention'
                ? t('Notification.you')
                : ''}
            </span>
          </div>
        ) : item?.entity_type === 'profile' ? (
          <>
            <div className="notification-message-container">
              <div
                className="notification-message"
                onClick={() => navigate(`/profile/${item.actionBy}`)}
              >
                {item.displayName}
              </div>
              <span className="notification-follow-username">
                · @{item.actionBy}
              </span>
            </div>
            <div className="notification-post-description">{item.headline}</div>
            <span className="notification-follow-message">
              {item.action === 'followrequest'
                ? t('Notification.SentFollowReq')
                : item.action === 'followaccept'
                ? t('Notification.AcceptFollowReq')
                : ''}
            </span>
          </>
        ) : item?.entity_type === 'job' ? (
          <>
            <div className="notification-message-container">
              {item.action === 'apply' ? (
                <div>
                  {item.count <= 2
                    ? t('Notification.ApplyStarted')
                    : `${item.count - 1}+ ${t('Notification.MoreApplicant')}`}
                </div>
              ) : (
                <>
                  <div
                    className="notification-message"
                    onClick={() => navigate(`/profile/${item.actionBy}`)}
                  >
                    {item.displayName}
                  </div>
                  {t('Notification.JobApplicationAction')}
                </>
              )}
            </div>
            <div className="notification-post-description">
              {item.jobTitle} · {item.companyName}
            </div>
            <span className="notification-follow-message">
              {item.action === 'shortlist'
                ? t('Notification.ProfileSelected')
                : item.action === 'followaccept'
                ? t('Notification.AcceptFollowReq')
                : ''}
            </span>
          </>
        ) : item?.entity_type === 'businesshub' ? (
          <>
            <div className="notification-message-container">
              {item.action === 'approved' ? (
                <div>
                  Your Business Hub page has been approved successfully 🎉
                </div>
              ) : item.action === 'rejected' ? (
                <div>
                  Your Business Hub page has been rejected. Please provide more
                  information
                </div>
              ) : (
                <></>
              )}
            </div>
          </>
        ) : (
          <></>
        )}
        <span className="notification-created-at">
          {formatDateTime(item?.createdAt)}
        </span>
        <div className="notification-menu-container">
          <CustomPopover
            isOpen={openPopover}
            handlePopoverClick={() => {
              setOpenPopover(true);
            }}
            action={<BasicIconButton icon={<More />} size="small" />}
            content={notificationMenu(item?.id)}
          />
        </div>
      </>
    );
  };

  const handleRenderDescription = (item) => {
    if (item?.entity_type === 'post' || item?.entity_type === 'businesshub') {
      return (
        <>
          <div
            className="notification-post-description"
            dangerouslySetInnerHTML={{
              __html: item?.description || item?.businessMessage,
            }}
          />
          {(item?.action === 'comment' || item?.action === 'reply') && (
            <div className="notification-comment">{item?.[item?.action]}</div>
          )}
        </>
      );
    }
  };

  const handleClickNotification = (item) => {
    dispatch(
      handleUpdateNotificationRead({
        id: item?.id,
      })
    );
    navigate(
      item.action === 'followrequest'
        ? '/network/follow-request'
        : item.action === 'followaccept'
        ? '/network/following'
        : item.action === 'shortlist'
        ? '/jobs/applied-status'
        : item.action === 'apply'
        ? `/jobs/applied-list?id=${item.entity_id}`
        : (item.action === 'approved' || item.action === 'rejected')
        ? `/profile/business-hub`
        : `/post-details/${item.entity_id}`
    );
  };

  const handleCallNewNotification = () => {
    setNotificationList([]);
    handlefetch(0);
    setIsNewNotification(false);
    setScrollData((prev) => ({
      ...prev,
      page: 1,
    }));
    const payload = {
      pageName: 'notification',
      last_viewed_at: moment().utc().format('YYYY-MM-DD HH:mm:ss'),
    };
    dispatch(handleUpdateNotificationLastSeen(payload));
  };

  return (
    <div className="notification-content">
      <Button
        label="New Notification"
        onClick={handleCallNewNotification}
        className={clsx(
          isNewNotification ? 'notification-btn' : 'hide-notification-btn',
          'notification-new-btn'
        )}
      />
      {notificationList?.length > 0 && (
        <Virtuoso
          data={notificationList}
          useWindowScroll
          components={{
            Footer: () => {
              return (
                !isHasMore && (
                  <EndStyle>
                    <span>* * *</span>
                  </EndStyle>
                )
              );
            },
          }}
          endReached={
            isHasMore && scrollData.page !== 0 ? fetchMoreData : () => {}
          }
          itemContent={(_, item) => (
            <div
              className={clsx(
                'notification-user-item',
                item.read === 0 && 'notification-unseen-bg'
              )}
            >
              {loading ? (
                <Skeleton
                  variant="circular"
                  width={50}
                  height={50}
                  animation="wave"
                />
              ) : (
                <div className="notification-img-container">
                  {item.action !== 'apply' && item?.action !== 'shortlist' && (
                    <UserProfile width={50} height={50}>
                      {item?.profileImage ? (
                        <img
                          src={item?.profileImage}
                          className="profile-img"
                          alt="user-profile-picture"
                        />
                      ) : (
                        <Profile />
                      )}
                    </UserProfile>
                  )}
                  {item?.action === 'shortlist' && item?.companyLogo && (
                    <img
                      src={item?.companyLogo}
                      className="notification-company-logo"
                      alt="company-logo"
                    />
                  )}
                  {item?.action === 'apply' && !item?.companyLogo ? (
                    <img
                      src={item?.companyLogo}
                      className="notification-apply-company-logo"
                      alt="company-logo"
                    />
                  ) : (
                    item?.action === 'apply' && (
                      <SampleCompany className="notification-sample-company-logo" />
                    )
                  )}
                </div>
              )}
              <div className="notification-user-details">
                <div className="notification-displayName">
                  {loading ? (
                    <Skeleton width={200} animation="wave" />
                  ) : (
                    handleRenderTitleMsg(item)
                  )}
                </div>
                {item?.entity_type !== 'profile' && (
                  <div className="notification-description">
                    {loading ? (
                      <Skeleton width={200} animation="wave" />
                    ) : (
                      handleRenderDescription(item)
                    )}
                  </div>
                )}
                <Button
                  onClick={() => handleClickNotification(item)}
                  variant="outlined"
                  label={
                    item.action === 'followrequest'
                      ? t('Notification.ViewReq')
                      : item.action === 'followaccept'
                      ? t('Notification.ViewFollowing')
                      : item.action === 'shortlist'
                      ? t('Notification.ViewDetails')
                      : item.action === 'apply'
                      ? t('Notification.ViewApplicants')
                      : t('Notification.ViewPost')
                  }
                  className="notification-post-btn"
                  disableElevation
                />
              </div>
            </div>
          )}
        />
      )}
      {notificationList?.length === 0 &&
        loading &&
        [...Array(3)].map((index) => {
          return (
            <div key={index} className="notification-user-container">
              <div className="notification-user-item">
                <Skeleton
                  variant="circular"
                  width={50}
                  height={50}
                  animation="wave"
                />
                <div className="notification-user-details">
                  <div className="notification-displayName">
                    <Skeleton width={200} animation="wave" />
                  </div>
                  <div className="notification-username">
                    <Skeleton width={200} animation="wave" />
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      {notificationList?.length === 0 && !loading && (
        <div className="no-data-found">
          <div>{t('Notification.NoNotification')}</div>
        </div>
      )}
    </div>
  );
}

export default function Notification() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { tabname } = useParams();
  const [activeKey, setActiveKey] = useState(0);

  const tabNameLower = tabname?.toLocaleLowerCase();
  const TAB_MAP = {
    all: 0,
    post: 1,
    job: 2,
    businesshub: 3,
  };

  const TABS_DISPLAY_LABELS = {
    all: t('Notification.All'),
    post: t('Notification.Post'),
    job: t('Notification.Job'),
    businesshub: t('Notification.BusinessHub'),
  };

  const TABS_REVERSE = Object.fromEntries(
    Object.entries(TAB_MAP).map(([k, v]) => [v, k])
  );

  useEffect(() => {
    if (
      tabNameLower &&
      Object.prototype.hasOwnProperty.call(TAB_MAP, tabNameLower)
    ) {
      setActiveKey(TAB_MAP[tabNameLower]);
    } else {
      setActiveKey(0);
    }
  }, [tabNameLower]);

  const tabList = useMemo(
    () =>
      Object.entries(TAB_MAP).map(([routeName, index]) => {
        return {
          label: TABS_DISPLAY_LABELS[routeName],
          value: index,
          content: <NotificationList tabNameLower={routeName} />,
        };
      }),
    []
  );

  const handleTabChangePath = (index) => {
    const routeName = TABS_REVERSE[index] || 'all';
    navigate(`../notification${routeName === 'all' ? '' : '/' + routeName}`);
  };

  return (
    <PostBox className="notification-page-container">
      <NotificationContainer className="NotificationContainer">
        <Tab
          tabList={tabList}
          handleTabChange={(key) => {
            setActiveKey(key);
            handleTabChangePath(key);
          }}
          callbackTabIndex={() => activeKey}
          activeKey={activeKey}
          keyChange={true}
          tabCustomcss="notificationTab"
        />
      </NotificationContainer>
    </PostBox>
  );
}
